import { MessageSquare, PenLine } from 'lucide-react';
import { Search } from 'iconoir-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidebar } from '../../ui/sidebar';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { useTheme } from 'components/core/theme-provider';
import { SidebarPrimaryNavItem } from '../layout/sidebar-primary-nav-item';
import { isPathActive } from 'utils/is-path-active';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { groupConversations } from '../layout/hooks/useChatSidebar';
import { useChatStore } from '../../../state/store/chat/use-chat-store';

type ConversationSearchProps = {
  variant?: 'header' | 'sidebar-nav';
  showText?: boolean;
};

export function ConversationSearch({
  variant = 'header',
  showText = true,
}: ConversationSearchProps) {
  const [open, setOpen] = useState(false);
  const setNewChat = useChatStore((state) => state.setNewChat);
  const disableNewChat = useChatStore((state) => state.disableNewChat);
  const [search, setSearch] = useState('');
  const fetchSearchResults = useChatStore((state) => state.fetchSearchResults);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setOpenMobile, isMobile } = useSidebar();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const { theme } = useTheme();

  useEffect(() => {
    fetchSearchResults(search, configurationSettings);
  }, [search, fetchSearchResults, configurationSettings]);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const openSearch = () => {
    navigate('/chat-history');
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const flatConversationList = useChatStore((state) => state.searchConversationsList);
  const groupedConversations = groupConversations(flatConversationList, t);

  const searchNavLabel = t('SEARCH_NAV', { defaultValue: 'Search' });

  return (
    <>
      {variant === 'sidebar-nav' ? (
        <SidebarPrimaryNavItem
          label={searchNavLabel}
          icon="search"
          to="/chat-history"
          isActive={isPathActive(pathname, '/chat-history')}
          onClick={() => {
            if (isMobile) {
              setOpenMobile(false);
            }
          }}
          showText={showText}
        />
      ) : (
        <div className="h-full flex justify-center items-center">
          <button
            onClick={openSearch}
            className={`h-fullml-1 p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-primary-200/10' : 'hover:bg-primary-600/10'}`}
            aria-label={searchNavLabel}
          >
            <Search
              width={20}
              height={20}
              strokeWidth={2}
              className={theme === 'dark' ? 'text-[hsl(var(--grey-400))]' : 'text-[hsl(var(--grey-600))]'}
            />
          </button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showOverlay={true}
          className="p-0 gap-0 w-[calc(100%-2rem)] max-w-[600px] sm:w-full sm:max-w-[45%] rounded-lg shadow-lg border mx-auto"
        >
          <Command className="rounded-lg">
            <CommandInput
              placeholder={`${t('SEARCH_CHATS')}...`}
              value={search}
              onValueChange={setSearch}
              className="flex-1 py-3 border-0 pr-4"
            />
            <CommandList className="h-[400px] overflow-auto py-2">
              {groupedConversations.length === 0 && (
                <CommandEmpty>{t('NO_RESULTS_FOUND')}</CommandEmpty>
              )}

              <CommandGroup>
                <CommandItem
                  disabled={disableNewChat}
                  className={`${disableNewChat ? '!cursor-not-allowed [&_*]:!cursor-not-allowed opacity-50' : 'cursor-pointer'} flex items-center gap-2 px-4 py-3 group/new-chat`}
                  onSelect={() => {
                    if (disableNewChat) return;
                    setOpen(false);
                    setNewChat(true);
                    navigate('/chat');
                  }}
                >
                  <PenLine className="h-4 w-4 text-muted-foreground group-hover/new-chat:text-foreground" />
                  <span className="truncate text-sm group-hover/new-chat:text-foreground">
                    {t('NEW_CHAT')}
                  </span>
                </CommandItem>
              </CommandGroup>

              {groupedConversations.map((group) => (
                <CommandGroup key={group.id} heading={group.label}>
                  {group.items.map((chat) => (
                    <CommandItem
                      key={chat.ConversationId}
                      value={`${chat.ConversationTitle}-${chat.ConversationId}`}
                      className="flex items-center gap-2 px-4 py-3 cursor-pointer group/item"
                      data-conversation-id={chat.ConversationId}
                      onSelect={() => {
                        setOpen(false);
                        if (chat?.ProjectId) {
                          navigate(`/chat/${chat.ConversationId}/${chat.ProjectId}`);
                        } else {
                          navigate(`/chat/${chat.ConversationId}`);
                        }
                      }}
                    >
                      <MessageSquare className="h-4 w-4 text-muted-foreground group-hover/item:text-foreground" />
                      <span className="truncate text-sm group-hover/item:text-foreground">
                        {chat.ConversationTitle}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
